/**
 * @spec docs/specs/features/staking-position-ui.md#phase-2-transactions
 * Chain configuration and network utilities
 */
import { env } from './env';

/**
 * Chain configuration
 */
export interface ChainConfig {
  id: number;
  name: string;
  rpcUrl: string;
  explorerUrl: string;
  explorerName: string;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
}

/**
 * Supported chains
 */
export const CHAIN_CONFIG = {
  BASE: {
    id: 8453,
    name: 'Base',
    rpcUrl: env.baseRpcUrl,
    explorerUrl: env.baseExplorerUrl,
    explorerName: 'Basescan',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  },
  BASE_SEPOLIA: {
    id: 84532,
    name: 'Base Sepolia',
    rpcUrl: env.baseRpcUrl,
    explorerUrl: env.baseExplorerUrl,
    explorerName: 'Basescan',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  },
  POLYGON: {
    id: 137,
    name: 'Polygon',
    rpcUrl: env.polygonRpcUrl,
    explorerUrl: env.polygonExplorerUrl,
    explorerName: 'Polygonscan',
    nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
  },
  POLYGON_AMOY: {
    id: 80002,
    name: 'Polygon Amoy',
    rpcUrl: env.polygonRpcUrl,
    explorerUrl: env.polygonExplorerUrl,
    explorerName: 'Polygonscan',
    nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
  },
} as const satisfies Record<string, ChainConfig>;

export type SupportedChainId = (typeof CHAIN_CONFIG)[keyof typeof CHAIN_CONFIG]['id'];

/**
 * Get chain config by ID
 */
export function getChainById(chainId: number): ChainConfig | undefined {
  return Object.values(CHAIN_CONFIG).find((chain) => chain.id === chainId);
}

/**
 * Check if a chain ID is supported
 */
export function isSupportedChain(chainId: number): chainId is SupportedChainId {
  return Object.values(CHAIN_CONFIG).some((chain) => chain.id === chainId);
}

/**
 * Get explorer URL for a transaction
 */
export function getTransactionUrl(chainId: number, txHash: string): string {
  const chain = getChainById(chainId);
  if (!chain) return '';
  return `${chain.explorerUrl}/tx/${txHash}`;
}

/**
 * Get explorer URL for an address
 */
export function getAddressUrl(chainId: number, address: string): string {
  const chain = getChainById(chainId);
  if (!chain) return '';
  return `${chain.explorerUrl}/address/${address}`;
}

/**
 * Get the chain ID for staking operations (Base mainnet or testnet)
 */
export function getStakingChainId(): number {
  // Use Base Sepolia if RPC URL contains 'sepolia', otherwise mainnet
  if (env.baseRpcUrl?.includes('sepolia')) {
    return CHAIN_CONFIG.BASE_SEPOLIA.id;
  }
  return CHAIN_CONFIG.BASE.id;
}

/**
 * Get the chain ID for USDT redemption (Polygon mainnet or testnet)
 */
export function getRedemptionChainId(): number {
  // Use Polygon Amoy if RPC URL contains 'amoy', otherwise mainnet
  if (env.polygonRpcUrl?.includes('amoy')) {
    return CHAIN_CONFIG.POLYGON_AMOY.id;
  }
  return CHAIN_CONFIG.POLYGON.id;
}
